import { z } from "zod";
import { compilationThemeIdSchema, compilationThemeVersionSchema, } from "./compilation-theme/compilation-theme.js";
import { compilationOccasionIdSchema } from "./compilation-occasion.js";
import { compilationSongSchema } from "./compilation-song.js";
export const volumeSchema = z.union([
    z.literal("mute"),
    z.literal("low"),
    z.literal("normal"),
]);
export const faceSchema = z.object({
    urls: z.array(z.string().url()),
    width: z.number(),
    height: z.number(),
    durationSeconds: z.number(),
    faceLocations: z
        .object({
        timeInSeconds: z.number(),
        embedding: z.number().array().optional(),
        score: z.number(),
        boxRaw: z.tuple([z.number(), z.number(), z.number(), z.number()]),
    })
        .array(),
});
export const clipRotationAngleLiterals = [
    z.literal(0),
    z.literal(90),
    z.literal(180),
    z.literal(270),
];
export const clipRotationAngles = clipRotationAngleLiterals.map((x) => x.value);
const renderConfigClipCommonSchema = z.object({
    segmentId: z.string(),
    rotateAngleDegrees: z.union(clipRotationAngleLiterals).default(0),
    caption: z.object({
        title: z.string().optional(),
        subtitle: z.string().optional(),
    }),
    urls: z.array(z.string().url()),
    skipFace: z.boolean().default(false),
    // It should be possible to derive these values from the pipe uploaded asset
    durationSeconds: z.number().default(5),
    width: z.number(),
    height: z.number(),
    // Undefined if the face detection has not been run
    faces: faceSchema.array().optional(),
    thumbnailSrc: z.string().url().optional(),
    // The user is not allowed to edit this segment and it won't be included in a
    // compilation
    isLocked: z.boolean().optional(),
});
const renderConfigVideoClipSchema = renderConfigClipCommonSchema.extend({
    type: z.literal("video"),
    trimNormalized: z.object({
        start: z.number().gte(0).lte(1).default(0),
        end: z.number().gte(0).lte(1).default(1),
    }),
    muteMusic: z.boolean().default(false),
});
const renderConfigPhotoClipSchema = renderConfigClipCommonSchema.extend({
    type: z.literal("photo"),
});
export const renderConfigClipSchema = z.union([
    renderConfigVideoClipSchema,
    renderConfigPhotoClipSchema,
]);
export const videoFrameSchema = z.object({
    width: z.number().default(1920),
    height: z.number().default(1080),
    fps: z.number().default(30),
});
export const renderConfigLogoSizes = ["small", "medium", "large"];
export const renderConfigLogoSchema = z.object({
    enabled: z.boolean(),
    size: z.enum(renderConfigLogoSizes).default("medium"),
    // The location where the logo was originally uploaded. This should be S3
    // (unless we've moved onto another storage location). The reason we keep this
    // around is so that when we copy a renderConfig when creating a new
    // compilation, we can restore this URL.
    //
    // NOTE: We could make this a discriminated union where `originalUploadUrl`
    // and urls are empty when enabled=false, but we don't want to clear
    // `originalUploadUrl` or `urls` so that if the re-enabled, their last
    // uploaded logo will still be there.
    originalUploadUrl: z.string().url().optional(),
    // This array should not be stored in the database. It should be derived from
    // originalUploadUrl (basically: [cndUrl, originalUploadUrl])
    urls: z.array(z.string().url()),
});
export const renderConfigUserInputValueSchema = z.string();
export const renderConfigUserInputSchema = z.record(z.string(), renderConfigUserInputValueSchema);
const staticAssetBaseUrlsSchema = z.object({
    sourceBaseUrl: z.url(),
    cdnBaseUrl: z.url().optional(),
});
export const renderConfigSchema = z.object({
    version: z.literal(1),
    occasionId: compilationOccasionIdSchema,
    themeId: compilationThemeIdSchema,
    themeVersion: compilationThemeVersionSchema,
    staticAssetBaseUrls: staticAssetBaseUrlsSchema.optional(),
    videoFrame: videoFrameSchema,
    introGreeting: z.object({
        enabled: z.boolean(),
        messages: z.array(z.string()),
    }),
    outroGreeting: z.object({
        enabled: z.boolean(),
        messages: z.array(z.string()),
    }),
    userInput: renderConfigUserInputSchema.optional(),
    faceCollageEnabled: z.boolean(),
    music: z.object({
        enabled: z.boolean(),
        volumeDuringClips: volumeSchema.optional(),
        song: compilationSongSchema,
    }),
    logo: renderConfigLogoSchema,
    // this is the outro branding which is different from the logo
    branding: z.object({
        enabled: z.boolean().default(true),
    }),
    clips: renderConfigClipSchema.array(),
});
export const renderConfigLogoDefaultState = {
    enabled: false,
    size: "medium",
    urls: [],
};
export const BUNDLES_KEY_PREFIX = "bundles";
export const LATEST_BUNDLE_FILENAME = "latest";
export const LATEST_BUNDLE_KEY = `${BUNDLES_KEY_PREFIX}/${LATEST_BUNDLE_FILENAME}`;
